import React, { useCallback } from 'react';
import { useGetDefaultDirectLaborTypes } from '../../hooks/useDefaultCloseoutHooks';
import {
  useCreateDirectLaborChangeOrderCloseoutMutation,
  useCreateDirectLaborCloseoutMutation,
  useGetDirectLaborChangeOrderCloseouts,
  useGetDirectLaborCloseouts,
} from '../../hooks/useCloseoutHooks';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { formatNumber } from './closeoutHelpers';
import { DirectLaborCloseout } from '../../api/protosCompiled/estimateCloseout/estimateCloseout_pb';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  SelectChangeEvent,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import { DirectLaborRow } from './DirectLaborRow';
import { DirectLaborChangeOrderCloseout } from '../../api/protosCompiled/changeOrderCloseout/changeOrderCloseout_pb';
import { useLockBid } from '../../utils/useLockBid';

export const DirectLaborHeader = () => {
  const theme = useTheme();

  // Common styles matching TotalPricingRow and TotalPricingHeader
  const headerCellStyle = {
    fontWeight: 600,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    padding: theme.spacing(1.5, 2),
    whiteSpace: 'nowrap' as const,
    borderBottom: `1px solid ${theme.palette.divider}`,
  };
  const { lockBid } = useLockBid();
  //zustand state
  const selectedEstimate = useStore(useUnityBuildStore).selectedEstimate;
  const selectedChangeOrder = useStore(useUnityBuildStore).selectedChangeOrder;

  // React-query state & hooks
  const { data: laborDefaults } = useGetDefaultDirectLaborTypes();

  const { data: estimateLabor, refetch: refetchEstimateLabor } = useGetDirectLaborCloseouts();

  const { data: changeOrderLabor, refetch: refetchChangeOrderLabor } =
    useGetDirectLaborChangeOrderCloseouts();

  const { mutateAsync: createEstimateLabor } = useCreateDirectLaborCloseoutMutation();

  const { mutateAsync: createChangeOrderLabor } = useCreateDirectLaborChangeOrderCloseoutMutation();

  const refetch = selectedEstimate?.estimateid ? refetchEstimateLabor : refetchChangeOrderLabor;

  const createLabor = selectedEstimate?.estimateid ? createEstimateLabor : createChangeOrderLabor;

  const closeout = selectedEstimate?.estimateid ? estimateLabor : changeOrderLabor;

  const newCloseoutType = selectedEstimate?.estimateid
    ? new DirectLaborCloseout()
    : new DirectLaborChangeOrderCloseout();

  const takeoffId = selectedEstimate?.estimateid
    ? selectedEstimate?.estimateid
    : (selectedChangeOrder?.changeorderid ?? '');

  const handleAddDefaultType = useCallback(
    (e: SelectChangeEvent<string>, child: React.ReactNode) => {
      const newValue = e.target.value;
      const defaultLaborType = laborDefaults?.find(laborType => laborType.name === newValue);
      if (defaultLaborType) {
        newCloseoutType.setCloseoutid(defaultLaborType.id);
        newCloseoutType.setEstimateid(takeoffId);
        newCloseoutType.setCloseoutdefaultdirectlaborid(defaultLaborType.id);
        newCloseoutType.setLaborrate(defaultLaborType.laborrate);
        newCloseoutType.setBurdenpercent(defaultLaborType.burdenpercent);
        newCloseoutType.setFringe(defaultLaborType.fringe);
        newCloseoutType.setDistributionpercent(defaultLaborType.distributionpercent);
        newCloseoutType.setNotes('');
        createLabor(newCloseoutType, {
          onSuccess: () => {
            refetch();
          },
        }).catch(err => console.error('Error creating direct labor:', err));
      }
    },
    // eslint-disable-next-line
    [laborDefaults, selectedEstimate, selectedChangeOrder, createLabor, refetch]
  );

  const totalDistributionPercent = closeout?.closeoutsList.reduce(
    (sum, labor) => sum + labor.distributionpercent,
    0
  );

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.default,
      }}
      role="region"
      aria-label="Direct Labor Management"
    >
      <FormControl
        fullWidth
        variant="filled"
        sx={{
          mb: 1,
          '& .MuiInputLabel-root': {
            color: theme.palette.text.secondary,
            '&.Mui-focused': {
              color: theme.palette.primary.main,
            },
          },
          '& .MuiFilledInput-root': {
            backgroundColor: theme.palette.background.paper,
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.05)
                  : alpha(theme.palette.common.black, 0.02),
            },
            '&.Mui-focused': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.08)
                  : alpha(theme.palette.common.black, 0.05),
            },
          },
          '& .MuiSelect-select': {
            color: theme.palette.text.primary,
          },
        }}
      >
        <Tooltip title="Choose a labor type to add to the table" arrow>
          <InputLabel id="labor-select-label">Select Labor Type</InputLabel>
        </Tooltip>
        <Select
          labelId="labor-select-label"
          id="labor-select"
          label="Select Labor Type"
          value={'Select Labor Type'}
          onChange={handleAddDefaultType}
          aria-label="Select a labor type to add"
          disabled={lockBid()}
        >
          <MenuItem value="">
            <em>Select Labor Type</em>
          </MenuItem>
          {laborDefaults &&
            laborDefaults.map(laborType => (
              <MenuItem
                key={laborType.id}
                value={laborType.name}
                aria-label={`Add ${laborType.name} labor type`}
              >
                {laborType.name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <Box
        sx={{ flex: 1, overflow: 'hidden' }}
        role="region"
        aria-label="Direct Labor Entries Table"
      >
        <TableContainer
          component={Paper}
          sx={{
            height: '100%',
            overflowY: 'auto',
            backgroundColor: theme.palette.background.default,
            borderColor: theme.palette.divider,
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              width: '100%',
              tableLayout: 'fixed',
              '& .MuiTableCell-root': {
                borderColor: theme.palette.divider,
              },
            }}
            aria-label="Direct Labor Entries"
          >
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellStyle}>
                  <Tooltip title="Name or description of the labor entry" arrow>
                    <span>Description</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={headerCellStyle}>
                  <Tooltip title="Total hours allocated for this labor type" arrow>
                    <span>
                      Hours: {formatNumber(closeout?.closeoutsList[0]?.totaldirectlaborhours || 0)}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    ...headerCellStyle,
                    color: totalDistributionPercent
                      ? totalDistributionPercent < 100
                        ? theme.palette.error.main
                        : totalDistributionPercent > 100
                          ? theme.palette.warning.main
                          : theme.palette.success.main
                      : theme.palette.text.primary,
                  }}
                >
                  <Tooltip title="Percentage distribution of labor costs" arrow>
                    <span>Distribution %: {formatNumber(Number(totalDistributionPercent))}</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={headerCellStyle}>
                  <Tooltip title="Hourly rate for this labor type" arrow>
                    <span>Labor Rate</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={headerCellStyle}>
                  <Tooltip title="Total cost before burden and fringe" arrow>
                    <span>Subtotal</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={headerCellStyle}>
                  <Tooltip title="Percentage of burden applied to labor cost" arrow>
                    <span>Burden %</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={headerCellStyle}>
                  <Tooltip title="Additional fringe benefits cost per hour" arrow>
                    <span>Fringe</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={headerCellStyle}>
                  <Tooltip title="Total burden cost calculated from burden percentage" arrow>
                    <span>Burden Total</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={headerCellStyle}>
                  <Tooltip title="Total fringe benefits cost" arrow>
                    <span>Fringe Total</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={headerCellStyle}>
                  <Tooltip title="Total cost per hour including burden and fringe" arrow>
                    <span>Hourly Cost</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={headerCellStyle}>
                  <Tooltip title="Total cost including all burden and fringe" arrow>
                    <span>Total Cost</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={headerCellStyle}>
                  <Tooltip title="Actions available for this labor entry" arrow>
                    <span>Actions</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {closeout?.closeoutsList.map((labor, index) => (
                <DirectLaborRow key={labor.closeoutid || index} labor={labor} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default DirectLaborHeader;
